/**
 * SVG chart primitives (react-native-svg, web+native) ported from the console:
 * Donut, Sparkline area, DonutRing (single-value gauge), and a Bars chart.
 */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle, Polyline, Polygon, G, Line, Defs, LinearGradient, Stop, Path } from 'react-native-svg';
import { useTheme } from '../../context/themeContext';

/* ── Donut (multi-segment) ── */
export const Donut: React.FC<{ title: string; segments: Array<{ label: string; value: number; color: string }> }> = ({ title, segments }) => {
  const { theme: { colors, font } } = useTheme();
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = 56, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <View style={[ch.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 18 }}>
        <View style={{ width: 140, height: 140 }}>
          <Svg width="140" height="140" viewBox="0 0 140 140">
            <G transform="translate(70,70) rotate(-90)">
              {segments.map((s, i) => {
                const len = (s.value / total) * c;
                const el = <Circle key={i} r={r} cx="0" cy="0" fill="none" stroke={s.color} strokeWidth="18" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} />;
                offset += len;
                return el;
              })}
            </G>
          </Svg>
          <View style={ch.donutLabel}>
            <Text style={{ color: colors.ink, fontSize: 20, fontWeight: '700' }}>{total.toLocaleString()}</Text>
            <Text style={{ color: colors.ink4, fontSize: 10 }}>total</Text>
          </View>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={{ color: colors.ink, fontSize: 13, fontWeight: '600', marginBottom: 10 }}>{title}</Text>
          {segments.map((s, i) => (
            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: s.color }} />
              <Text style={{ flex: 1, color: colors.ink2, fontSize: 12 }}>{s.label}</Text>
              <Text style={{ color: colors.ink3, fontSize: 12, fontFamily: font.mono }}>{((s.value / total) * 100).toFixed(0)}%</Text>
              <Text style={{ color: colors.ink, fontSize: 12, fontFamily: font.mono, fontWeight: '600', minWidth: 40, textAlign: 'right' }}>{s.value.toLocaleString()}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

/* ── Single-value gauge ring (Quota drawer) ── */
export const GaugeRing: React.FC<{ pct: number; used: number; limit: number; color: string }> = ({ pct, used, limit, color }) => {
  const { theme: { colors } } = useTheme();
  const r = 56, circ = 2 * Math.PI * r;
  const dash = (Math.min(100, pct) / 100) * circ;
  return (
    <View style={{ width: 140, height: 140 }}>
      <Svg width="140" height="140" viewBox="0 0 140 140">
        <Circle r={r} cx="70" cy="70" fill="none" stroke={colors.paper3} strokeWidth="14" />
        <G transform="translate(70,70) rotate(-90)">
          <Circle r={r} cx="0" cy="0" fill="none" stroke={color} strokeWidth="14" strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round" />
        </G>
      </Svg>
      <View style={ch.donutLabel}>
        <Text style={{ color: colors.ink, fontSize: 18, fontWeight: '700' }}>{used}<Text style={{ color: colors.ink4 }}>/{limit}</Text></Text>
        <Text style={{ color: colors.ink4, fontSize: 10 }}>{pct.toFixed(1)}% used</Text>
      </View>
    </View>
  );
};

/* ── Sparkline area ── */
export const Sparkline: React.FC<{ data: number[]; color?: string; height?: number }> = ({ data, color = '#1E3A5F', height = 40 }) => {
  if (data.length < 2) return null;
  const w = 120, max = Math.max(...data), min = Math.min(...data), range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  const gid = `sp-${color.replace('#', '')}`;
  return (
    <Svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none">
      <Defs>
        <LinearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <Stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <Stop offset="100%" stopColor={color} stopOpacity="0" />
        </LinearGradient>
      </Defs>
      <Polygon points={`0,${height} ${pts} ${w},${height}`} fill={`url(#${gid})`} />
      <Polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
};

/* ── 7-day bars (Quota drawer) ── */
export const Bars: React.FC<{ history: Array<{ day: string; email: number; whatsapp: number }> }> = ({ history }) => {
  const { theme: { colors } } = useTheme();
  const max = Math.max(...history.flatMap(h => [h.email, h.whatsapp]), 1);
  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 8, height: 100 }}>
        {history.map((d, i) => (
          <View key={i} style={{ flex: 1, alignItems: 'center', gap: 4 }}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 3, flex: 1 }}>
              <View style={{ width: 6, height: `${(d.email / max) * 100}%`, minHeight: 2, backgroundColor: '#1E3A5F', borderRadius: 1 }} />
              <View style={{ width: 6, height: `${(d.whatsapp / max) * 100}%`, minHeight: 2, backgroundColor: '#25D366', borderRadius: 1 }} />
            </View>
            <Text style={{ color: colors.ink4, fontSize: 10 }}>{d.day}</Text>
          </View>
        ))}
      </View>
      <View style={{ flexDirection: 'row', gap: 16, marginTop: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}><View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#1E3A5F' }} /><Text style={{ color: colors.ink3, fontSize: 11 }}>Email</Text></View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}><View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#25D366' }} /><Text style={{ color: colors.ink3, fontSize: 11 }}>WhatsApp</Text></View>
      </View>
    </View>
  );
};

/* ── Multi-line delivery chart (Health) ── */
export const DeliveryLines: React.FC<{ delivered: number[]; partial: number[]; failed: number[] }> = ({ delivered, partial, failed }) => {
  const { theme: { colors } } = useTheme();
  const W = 720, H = 200, P = { l: 36, r: 16, t: 16, b: 28 };
  const innerW = W - P.l - P.r, innerH = H - P.t - P.b;
  const maxY = Math.max(10, Math.max(...delivered, ...partial, ...failed, 0) + 10);
  const step = innerW / Math.max(1, delivered.length - 1);
  const pathFor = (arr: number[]) => arr.map((v, i) => `${i === 0 ? 'M' : 'L'}${(P.l + i * step).toFixed(1)},${(P.t + innerH - (v / maxY) * innerH).toFixed(1)}`).join(' ');
  const areaFor = (arr: number[]) => `${pathFor(arr)} L${P.l + (arr.length - 1) * step},${P.t + innerH} L${P.l},${P.t + innerH} Z`;
  const yTicks = [0, 25, 50, 75, 100];
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <Defs>
        <LinearGradient id="g-del" x1="0" x2="0" y1="0" y2="1">
          <Stop offset="0%" stopColor="#2E7D32" stopOpacity="0.18" /><Stop offset="100%" stopColor="#2E7D32" stopOpacity="0" />
        </LinearGradient>
      </Defs>
      {yTicks.map((t, i) => {
        const y = P.t + innerH - (t / maxY) * innerH;
        return <Line key={i} x1={P.l} x2={W - P.r} y1={y} y2={y} stroke={colors.line} strokeWidth="1" />;
      })}
      <Path d={areaFor(delivered)} fill="url(#g-del)" />
      <Path d={pathFor(delivered)} fill="none" stroke="#2E7D32" strokeWidth="2" />
      <Path d={pathFor(partial)} fill="none" stroke="#F57C00" strokeWidth="2" />
      <Path d={pathFor(failed)} fill="none" stroke="#C0392B" strokeWidth="2" />
    </Svg>
  );
};

const ch = StyleSheet.create({
  card: { padding: 16, borderRadius: 2, borderWidth: 1 },
  donutLabel: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' },
});
